import {isFunction, callback} from 'chart.js/helpers';
import {getElements} from './interaction';
import {loadHooks} from './helpers';

const moveHooks = ['enter', 'leave'];

/**
 * @typedef { import("chart.js").Chart } Chart
 * @typedef { import('../../types/options').AnnotationPluginOptions } AnnotationPluginOptions
 */

export const eventHooks = moveHooks.concat('click');

/**
 * @param {Chart} chart
 * @param {Object} state
 * @param {AnnotationPluginOptions} options
 */
export function updateListeners(chart, state, options) {
    state.listened = loadHooks(options, eventHooks, state.listeners);
    state.moveListened = false;

    moveHooks.forEach(hook => {
        if (isFunction(options[hook])) {
            state.moveListened = true;
        }
    });

    if (!state.listened || !state.moveListened) {
        state.annotations.forEach(scope => {
            if (!state.listened && isFunction(scope.click)) {
                state.listened = true;
            }
            if (!state.moveListened) {
                moveHooks.forEach(hook => {
                    if (isFunction(scope[hook])) {
                        state.listened = true;
                        state.moveListened = true;
                    }
                });
            }
        });
    }
}

/**
 * @param {Object} state
 * @param {ChartEvent} event
 * @param {AnnotationPluginOptions} options
 * @return {boolean|undefined}
 */
export function handleEvent(state, event, options) {
    if (state.listened) {
        switch (event.type) {
            case 'mousemove':
            case 'mouseout':
                return handleMoveEvents(state, event, options);
            case 'click':
                return handleClickEvents(state, event, options);
            default:
        }
    }
}

function handleMoveEvents(state, event, options) {
    if (!state.moveListened) {
        return;
    }

    let elements;

    if (event.type === 'mousemove') {
        elements = getElements(state.visibleElements, event, options.interaction);
    } else {
        elements = [];
    }

    const previous = state.hovered;
    state.hovered = elements;

    const context = {state, event};
    let changed = dispatchMoveEvents(context, 'leave', previous, elements);
    return dispatchMoveEvents(context, 'enter', elements, previous) || changed;
}

function dispatchMoveEvents({state, event}, hook, elements, checkElements) {
    let changed;
    for (const element of elements) {
        if (checkElements.indexOf(element) < 0) {
            changed = dispatchEvent(element.options[hook] || state.listeners[hook], element, event) || changed;
        }
    }
    return changed;
}

function handleClickEvents(state, event, options) {
    const listeners = state.listeners;
    const elements = getElements(state.visibleElements, event, options.interaction);
    let changed;
    for (const element of elements) {
        changed = dispatchEvent(element.options.click || listeners.click, element, event) || changed;
    }
    return changed;
}

function dispatchEvent(handler, element, event) {

    /**
     * Here we added our Micromate fix. The line before looked like this:
     *  return callback(handler, [element.$context, event]) === true;
     *
     * As for whatever reason other libraries ended up in here (e.g. cookiebot or the chomr extension for mouse gestures)
     * we have to make sure the method can also handle events where element is undefined.
     */
    return callback(handler, [element?.$context, event]) === true;
}
